import { useState, useEffect } from 'react';

import {
  ShowPhoneNumberButtonLink,
  ShowPhoneNumberText,
} from 'components/Toolkit/ShowPhoneNumberButton/ShowPhoneNumberButton.styled';

import { useUserContext } from 'contexts/UserContext';
import { useVerificationContext } from 'contexts/VerificationContext';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';

import { CDN_STATIC_ASSETS } from '../../../utils';
import { Loading } from 'components/Toolkit/Loading/Loading';

import type { ShowPhoneNumberButtonProps } from 'components/Toolkit/ShowPhoneNumberButton/ShowPhoneNumberButton.typed';

const ShowPhoneNumberButton = (props: ShowPhoneNumberButtonProps) => {
  const {
    onReveal,
    showNumberTextVariant,
    size,
    trackingId,
    onCall,
    isPrimaryButton,
    adId,
    disabled,
  } = props;

  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useUserContext();
  const { triggerAfterVerify, setTriggerAfterVerify } =
    useVerificationContext();

  useOnUpdateOnly(() => {
    setPhone('');
  }, [adId]);

  async function handleReveal() {
    if (phone) {
      if (onCall) {
        onCall();
        return;
      } else {
        // If the phone is already available and we are
        // not wanting to do anything on the call then return
        return;
      }
    } else {
      setIsSubmitting(true);
      const phoneNumber = await onReveal();
      setIsSubmitting(false);
      if (phoneNumber) {
        setPhone(phoneNumber);
      }
    }
  }

  useEffect(() => {
    if (triggerAfterVerify && user) {
      setTriggerAfterVerify(false);
      handleReveal();
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAfterVerify]);

  return (
    <ShowPhoneNumberButtonLink
      size={size}
      ofType={isPrimaryButton ? 'PRIMARY' : 'SECONDARY'}
      data-testid={phone ? 'call-phone-number' : 'view-phone-number'}
      href={phone ? `tel:${phone}` : undefined}
      onClick={handleReveal}
      data-tracking={trackingId}
      isFakeLink={phone ? false : true}
      disabled={disabled}
    >
      {phone && !isSubmitting ? (
        <ShowPhoneNumberText>{`Call ${phone}`}</ShowPhoneNumberText>
      ) : isSubmitting ? (
        <Loading
          cdnUrl={CDN_STATIC_ASSETS}
          color={isPrimaryButton ? 'LIGHT' : 'DARK'}
          data-testid="phone-number-loading"
        />
      ) : showNumberTextVariant ? (
        'Show Number'
      ) : (
        'Show Phone Number'
      )}
    </ShowPhoneNumberButtonLink>
  );
};

export { ShowPhoneNumberButton };
